import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@/i18n/config';
import { getLocaleFromPath, getLocalePrefix, localizedPath, switchLocalePath } from '@/i18n/routes';

export function useLocale() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const locale = useMemo(() => getLocaleFromPath(pathname), [pathname]);
  const prefix = useMemo(() => getLocalePrefix(locale), [locale]);

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
    document.documentElement.lang = locale;
  }, [locale, i18n]);

  const setLocale = useCallback(
    (targetLocale: Locale) => {
      if (targetLocale === locale) return;
      navigate(switchLocalePath(pathname, hash, targetLocale));
    },
    [locale, pathname, hash, navigate],
  );

  const toLocalizedPath = useCallback(
    (logicalPath: string) => localizedPath(logicalPath, locale),
    [locale],
  );

  return { locale, prefix, setLocale, toLocalizedPath };
}
